import type { OutcomeDefinition } from "@repo/app-types/entities/outcome";
import type { MetadataFieldId } from "@repo/app-types/entities/signal";
import type { SignalMatch } from "@repo/app-types/entities/signalMatch";
import type { FC } from "react";
import { BaseCard } from "../../performanceReports/baseCard.js";
import {
  MatchListItem,
  RECENT_MATCHES_DISPLAY_LIMIT,
} from "../../transcriptViewer/matchListItem.js";
import {
  formatMetadataFieldKey,
  formatMetadataValue,
} from "../signalStatsFormatters.js";

/**
 * An entry in the ordered list of metadata fields used to resolve a field's
 * display label and chart color from its stable ID.
 */
export interface MetadataFieldLabel {
  fieldId: MetadataFieldId;
  fieldKey: string;
}

export const MetadataLine: FC<{
  metadata: Record<string, string | number | null>;
  fields: readonly MetadataFieldLabel[];
}> = ({ metadata, fields }) => {
  const entries = Object.entries(metadata)
    .map(([fieldId, value]) => {
      const field = fields.find((f) => f.fieldId === fieldId);
      if (!field) {
        return null;
      }
      return { key: field.fieldKey, value };
    })
    .filter((e): e is { key: string; value: string | number | null } =>
      Boolean(e),
    );
  if (entries.length === 0) {
    return null;
  }

  return (
    <p className="text-xs text-muted-foreground">
      {entries.map(({ key, value }, i) => (
        <span key={key}>
          {i > 0 ? <span className="text-gray-300"> &middot; </span> : null}
          <span className="font-medium text-gray-400">
            {formatMetadataFieldKey(key)}:
          </span>{" "}
          <span className="text-gray-600">{formatMetadataValue(value)}</span>
        </span>
      ))}
    </p>
  );
};

export const SignalRecentMatches: FC<{
  matches: readonly SignalMatch[];
  outcomeDef: OutcomeDefinition | undefined;
  onMatchClick: (match: SignalMatch) => void;
}> = ({ matches, outcomeDef, onMatchClick }) => {
  const visibleMatches = matches.slice(0, RECENT_MATCHES_DISPLAY_LIMIT);
  return (
    <div className="flex w-full flex-col lg:w-96 lg:shrink-0 xl:w-[25rem]">
      <BaseCard title="Recent matches">
        {visibleMatches.length === 0 ? (
          <div className="py-8 text-center">
            <p className="text-sm text-gray-600">No matches in this range.</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Try adjusting filters or the date range.
            </p>
          </div>
        ) : (
          <div className="-mx-4">
            {visibleMatches.map((match) => (
              <MatchListItem
                key={match.matchId}
                contactName={match.contactName}
                recordedAt={match.lastRecordedAt}
                teammateName={match.teammateName}
                bodyText={match.explanation ?? null}
                outcome={match.outcome}
                outcomeDef={outcomeDef}
                onClick={() => onMatchClick(match)}
                className="border-b border-border-light last:border-b-0"
              />
            ))}
          </div>
        )}
      </BaseCard>
    </div>
  );
};
